import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Grid, Box, Button, Typography } from '@mui/material';
import { Home } from '@mui/icons-material';
import Header from '../components/Header';
import StartTest from '../components/StartTest';
import TestInstructions from '../components/TestInstructions';
import AudioLoadingOverlay from '../components/AudioLoadingOverlay';
import { selectSelectedStudent } from '../store/selectors/studentSelectors';
import { RootState } from '../store';
import { fetchSessionData, selectCurrentSession, selectSessionStatus } from '../store/slices/sessionSlice';
import { audioPreloader, AudioPreloadProgress } from '../services/audioPreloader';
import { fetchPackageQuestions, PackageQuestion } from '../services/testSessionService';

const StartTestPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isFetchingSessionRef = useRef(false);
  
  const selectedStudent = useSelector(selectSelectedStudent);
  const selectedTest = useSelector((state: RootState) => state.tests.selectedTest);
  const currentSession = useSelector(selectCurrentSession);
  const sessionStatus = useSelector(selectSessionStatus);
  const testProgressState = useSelector((state: RootState) => state.testSessionProgress);

  // Audio preloading state
  const [audioProgress, setAudioProgress] = useState<AudioPreloadProgress>({
    isLoading: false,
    loaded: 0,
    total: 0
  });
  const [isStartingTest, setIsStartingTest] = useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  
  // Fetch session data using selectedTest from Redux
  const sessionId = selectedTest?.sessionId;
  useEffect(() => {
    if (sessionId && !isFetchingSessionRef.current) {
      isFetchingSessionRef.current = true;
      dispatch(fetchSessionData(sessionId) as any).finally(() => {
        isFetchingSessionRef.current = false;
      });
    }
  }, [sessionId, dispatch]);
  
  const activeSessionId = testProgressState.activeSessionId;
  useEffect(() => {
    if (activeSessionId && !selectedTest) {
      // This would be a good place to fetch the test details from API if needed
    }
  }, [activeSessionId, selectedTest]);
  
  // Check if session is completed and redirect to results
  useEffect(() => {
    if (currentSession && currentSession.status === 'COMPLETED') {
      navigate('/test-results');
      return;
    }
  }, [currentSession, navigate]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear any pending audio operations
      setAudioProgress({
        isLoading: false,
        loaded: 0,
        total: 0
      });
      setIsStartingTest(false);
    };
  }, []);

  // ALL CALLBACK HOOKS MUST BE DEFINED BEFORE CONDITIONAL RETURNS
  // Audio preloading callback
  const handleAudioProgress = useCallback((progress: AudioPreloadProgress) => {
    // Defensive check to prevent state updates on unmounted component
    try {
      setAudioProgress(progress);
    } catch (error) {
    }
  }, []);

  // Retry audio loading
  const handleRetryAudio = useCallback(async () => {
    if (!currentSession?.packageId) return;

    try {
      const questions = await fetchPackageQuestions(currentSession.packageId);
      const audioUrls = questions
        ?.map((q: PackageQuestion) => q.mediaUrl)
        .filter((url): url is string => Boolean(url)) || [];

      await audioPreloader.preloadAllAudio(audioUrls, handleAudioProgress);
    } catch (error) {
    }
  }, [currentSession?.packageId, handleAudioProgress]);

  // Skip audio loading and continue
  const handleSkipAudio = useCallback(() => {
    setAudioProgress({
      isLoading: false,
      loaded: 0,
      total: 0
    });
    startTestNavigation();
  }, []);

  // Navigate to test taking page
  const startTestNavigation = useCallback(() => {
    if (!selectedTest?.sessionId) {
      return;
    }

    setIsStartingTest(true);

    // Save the fact that test was started to localStorage for recovery purposes
    try {
      localStorage.setItem('lastStartedTest', selectedTest.sessionId);
      localStorage.setItem('lastTestStartTime', new Date().toISOString());
    } catch (error) {
    }

    // Navigate to test taking page - session data is already cached in Redux
    navigate('/test-taking');
  }, [selectedTest?.sessionId, navigate]);

  const handleStartTest = useCallback(async () => {
    if (!selectedTest?.sessionId || !currentSession) {
      return;
    }

    // Fetch questions to get audio URLs
    try {
      const questions = await fetchPackageQuestions(currentSession.packageId);

      if (!questions || questions.length === 0) {
        startTestNavigation();
        return;
      }

      // Extract audio URLs from questions
      const audioUrls = questions
        .map((q: PackageQuestion) => q.mediaUrl)
        .filter((url): url is string => Boolean(url));


      if (audioUrls.length === 0) {
        // No audio files, start test immediately
        startTestNavigation();
        return;
      }

      // Start audio preloading
      await audioPreloader.preloadAllAudio(audioUrls, handleAudioProgress);

      // Audio loading completed successfully
      setTimeout(() => {
        startTestNavigation();
      }, 500); // Brief delay to show success state

    } catch (error) {
      // Continue anyway - audio will load on demand
      startTestNavigation();
    }
  }, [selectedTest?.sessionId, currentSession, handleAudioProgress, startTestNavigation]);

  const handleBackToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // NOW WE CAN HAVE CONDITIONAL RETURNS
  
  // Add validation for selectedTest
  if (!selectedTest) {
    return (
      <Container>
        <Typography variant="h6">Please select a test first</Typography>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </Container>
    );
  }
  
  if (!selectedStudent) {
    return <div>Please select a student first</div>;
  }

  // If we have session data from the API call but no selectedTest, we can still proceed
  if (!selectedTest && !currentSession) {
    // Show loading while we fetch session data
    if (sessionStatus === 'loading') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <Typography variant="h6">Loading session data...</Typography>
        </Box>
      );
    }
    
    // Only show error if we've tried to fetch and failed
    if (sessionStatus === 'failed') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <Typography variant="h5" mb={2}>Session not found</Typography>
          <Typography variant="body1" mb={3}>The test session could not be loaded. Please return to the dashboard and try again.</Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </Box>
      );
    }
  }
  
  // Show loading state while fetching session data
  if (sessionStatus === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Typography variant="h6">Loading session data...</Typography>
      </Box>
    );
  }
  
  // Use session data if selectedTest is not available (refresh scenario)
  const testData = selectedTest || (currentSession ? {
    testId: currentSession.id,
    title: 'Test Session',
    color: '#1976d2',
    subject: 'mock',
    description: 'Test session from current session',
    totalQuestions: 0,
    // Remove hardcoded completedQuestions to let session data take precedence
    variant: undefined,
    status: 'open'
  } : null);
  
  if (!testData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Typography variant="h5" mb={2}>Test not found</Typography>
        <Typography variant="body1" mb={3}>The selected test could not be loaded. Please return to the dashboard and try again.</Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </Box>
    );
  }
  
  // These calculations now happen after we've confirmed testData exists
  const hasExistingProgress = !!(testProgressState.progress[testData.sessionId] &&
    Object.keys(testProgressState.progress[testData.sessionId]?.answers || {}).length > 0);
  
  // Compute progress metrics when available
  const existingProgress = testProgressState.progress[testData.sessionId];
  const answeredQuestionCount = existingProgress ? 
    Object.values(existingProgress.answers).filter(q => q.answer !== null).length : 0;
  const lastActive = existingProgress ? new Date(existingProgress.lastUpdated) : null;

  // Calculate completed questions from session answers
  const completedQuestionsFromSession = currentSession?.answers ? 
    currentSession.answers.filter(answer => answer.answer !== null).length : 0;
  
  // Enhanced test data with fallbacks for all critical fields
  const startTestData = {
    title: testData.title || 'Test Session',
    description: testData.description || `This is a ${testData.subject || 'mock'} test with ${testData.totalQuestions || 0} questions.`,
    variant: testData.variant,
    status: testData.status || 'open',
    color: testData.color || '#1976d2',
    completedQuestions: completedQuestionsFromSession || testData.completedQuestions || 0,
    totalQuestions: testData.totalQuestions || currentSession?.totalNoOfQuestions || 0
  };

  // All callbacks are now defined at the top before conditional returns

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Header selectedStudent={selectedStudent} />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Back Button */}
        <Button
          variant="contained"
          size="large"
          startIcon={<Home />}
          onClick={handleBackToDashboard}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>
        
        <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <StartTest
              testData={startTestData}
              progressPercentage={startTestData.totalQuestions > 0 ? (startTestData.completedQuestions / startTestData.totalQuestions * 100) : 0}
              isStarting={isStartingTest}
              onStartTest={handleStartTest}
              hasExistingProgress={hasExistingProgress}
              answeredQuestionCount={answeredQuestionCount}
              lastActive={lastActive ? lastActive.toLocaleString() : undefined}
              totalDuration={currentSession?.totalDuration}
              isLoadingAudio={audioProgress.isLoading}
              audioLoadProgress={{ loaded: audioProgress.loaded, total: audioProgress.total }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TestInstructions />
          </Grid>
        </Grid>
      </Container>

      {/* Audio Loading Overlay */}
      <AudioLoadingOverlay
        progress={audioProgress}
        onRetry={handleRetryAudio}
        onSkip={handleSkipAudio}
      />
    </Box>
  );
};

export default React.memo(StartTestPage);